<template>
  <component
    :is="instance.getFormType()"
    v-bind="{
      dataRefid: 'transactionFormType',
      instance,
      label: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.TYPE],
      restrictions,
    }"
    :readonly="isReadonly"
    @ready="(el: HTMLInputElement) => (typeFieldRef = el)"
  />
  <component
    :is="instance.getFormDate()"
    v-bind="{
      dataRefid: 'transactionFormDate',
      instance,
      label: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.DATE],
    }"
    :readonly="isReadonly"
  />
  <template v-if="isTypeFilled">
    <TransactionFormEntry
      v-for="index in entriesCount"
      v-bind="{ instance, index }"
      ref="entryFieldRef"
      :key="index"
      :readonly="isReadonly"
    />
  </template>
  <component
    :is="instance.getFormDescription()"
    v-if="isDescriptionVisible"
    v-bind="{
      dataRefid: 'transactionFormDescription',
      instance,
      label: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.DESCRIPTION],
    }"
    :readonly="isReadonly"
  />
  <UIButtonsGroup>
    <template #default="{ short }">
      <UIButtonClose :short @click="handleClose" />
      <UIButtonSave
        v-if="!isReadonly"
        data-refid="transactionFormSave"
        :disabled
        :loading
        :short
        @click="handleSave"
      />
    </template>
  </UIButtonsGroup>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, provide, ref, useTemplateRef } from 'vue'

import { ReadonlyMode, TransactionComputedEntry } from '@types'
import { TransactionClass } from '..'
import { EntityState } from '@/entities/utils/enums'

import {
  TRANSACTION_FIELD,
  TRANSACTION_FIELD_LABEL,
  TRANSACTION_ID_PREFIX,
} from '../utils/const'
import { ROUTE_NAME } from '@/const'
import {
  NOTIFICATION_DELAY,
  OVERLAY_DURATION,
  READONLY_MODE,
} from '@/const/common'

import { useNotifications } from '@/plugins/notification'

import { useTransactionsBunchStore } from '@/store/transactions/bunch'

import TransactionFormEntry from './TransactionFormEntry.vue'
import TransactionFormDate from './TransactionFormDate.vue'
import { UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'

type Props = {
  instance: TransactionClass
  restrictions?: string[]
}

type Emits = {
  close: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { progress, remove, update } = useNotifications()

const transactionsBunchStore = useTransactionsBunchStore()

const loading = ref(false)

const typeFieldRef = ref<HTMLInputElement>()
const dateFieldRef = useTemplateRef<typeof TransactionFormDate>('dateFieldRef')
const entryFieldRef =
  useTemplateRef<(typeof TransactionFormEntry)[]>('entryFieldRef')

const readonly = inject<ReadonlyMode>(READONLY_MODE)

const isReadonly = computed(
  () => readonly?.value || props.instance.settings?.readonly,
)

const disabled = computed(
  () =>
    ![EntityState.CHANGED, EntityState.NEW].includes(props.instance.state) || !isTypeFilled.value,
)

const isSavedTransaction = computed(() =>
  props.instance.id.startsWith(TRANSACTION_ID_PREFIX),
)
provide('isSavedTransaction', isSavedTransaction)

const isTypeFilled = computed(
  () => props.instance.field(TRANSACTION_FIELD.TYPE).value,
)
const isDescriptionVisible = computed(
  () =>
    isTypeFilled.value,
)

const entriesCount = computed(() => {
  const count = props.instance.settings?.entries.length
  return count && count > 4 ? 4 : count
})

const handleClose = () => {
  emit('close')
}

const handleSave = async () => {
  loading.value = true
  const isNew = props.instance.isNew
  const nid = await progress({
    message: `${isNew ? 'Creating' : 'Updating'} transaction`,
  })
  try {
    if (isNew) {
      await props.instance.store()
    } else {
      await props.instance.update()
    }
    await update(
      nid,
      {
        type: 'success',
        message: `Transaction ${
          props.instance.field(TRANSACTION_FIELD.DATE).value
        } ${isNew ? 'created' : 'updated'}`,
        ...(transactionsBunchStore.getList.has(props.instance.id)
          ? {
            link: {
              text: 'Open',
              to: {
                name: ROUTE_NAME.TRANSACTIONS_ITEM,
                params: { id: props.instance.id },
              },
            },
          }
          : {}),
      },
      NOTIFICATION_DELAY,
    )
    emit('close')
  } catch {
    await remove(nid)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  setTimeout(() => {
    if (!isTypeFilled.value) {
      typeFieldRef.value?.focus()
      return
    }

    if (!props.instance.field(TRANSACTION_FIELD.DATE).value) {
      dateFieldRef.value?.select()
      return
    }

    for (let i = 0; i < props.instance.entriesLength && i < 2; i++) {
      const entryKey = `entry${i + 1}` as keyof TransactionClass
      if (
        !(props.instance[entryKey] as TransactionComputedEntry)().value?.account_id ||
        !(props.instance[entryKey] as TransactionComputedEntry)().value?.amount ||
        !(props.instance[entryKey] as TransactionComputedEntry)().value?.asset_id
      ) {
        entryFieldRef.value?.[i]?.focus()
        break
      }
    }
  }, OVERLAY_DURATION)
})
</script>
